import { SetToothFullHeadMPRSliceEditorStateReq } from '@/shared/api/protocol-ts/api/core/svc_report_pb';
import type { ToothFullHeadMPRSlice } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import {
  makeSavedFullHeadMprView,
  type FullHeadMPRViewSnapshot,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type { FullHeadMprOriginalSlice } from '../config';

import {
  isSameFullHeadMprAnnotationPlane,
  makeFullHeadMprViewMessage,
  resolveFullHeadMprSeriesContext,
} from './fullHeadMprViewGeometry';
import { makeFullHeadMprViewSelectionRequest } from './makeFullHeadMprViewSelectionRequest';

export const makeFullHeadMprEditorStateRequest = ({
  snapshot,
  toothID,
  slices,
  original,
}: {
  snapshot: FullHeadMPRViewSnapshot;
  toothID: string;
  slices: ToothFullHeadMPRSlice[];
  original?: FullHeadMprOriginalSlice | null;
}): SetToothFullHeadMPRSliceEditorStateReq => {
  const { view } = snapshot;
  const sourceView = makeFullHeadMprViewMessage(view);
  const originalStepMm =
    original?.target.case === 'Series'
      ? original.target.value.SliceStepMm
      : undefined;
  const matchesOriginalStep = (slice: ToothFullHeadMPRSlice) => {
    const { Source: source } = slice;
    if (source.case !== 'Series' || !originalStepMm) {
      return true;
    }
    const { SliceStepMm: sliceStepMm } = source.value;
    return !sliceStepMm || sliceStepMm === originalStepMm;
  };
  const originalSlice = original
    ? slices.find((slice) => {
        if (original.target.case === 'SliceID') {
          return slice.ID === original.target.value;
        }
        if (original.target.case !== 'Series' || slice.Source.case === 'View') {
          return false;
        }
        const series =
          slice.Source.case === 'Series' ? slice.Source.value : slice;
        return (
          series.Orientation === original.target.value.Orientation &&
          series.OffsetMm === original.target.value.OffsetMm &&
          matchesOriginalStep(slice)
        );
      })
    : undefined;
  const originalMatches =
    original &&
    (original.target.case !== 'SliceID' || !!originalSlice) &&
    isSameFullHeadMprAnnotationPlane({
      isSeries:
        original.target.case === 'Series' ||
        (!!originalSlice && originalSlice.Source.case !== 'View'),
      left: original.view,
      right: view,
    });
  const exact = slices.find(
    (slice) =>
      slice.Source.case === 'View' && slice.Source.value.equals(sourceView),
  );
  const existing =
    (originalMatches ? originalSlice : undefined) ??
    exact ??
    slices.find((slice) => {
      const frame =
        slice.EditorState?.AnnotationView ??
        (slice.Source.case === 'View' ? slice.Source.value : undefined);
      const candidate = frame
        ? makeSavedFullHeadMprView(
            frame,
            resolveFullHeadMprSeriesContext({ slice }),
          )
        : null;
      return (
        candidate &&
        matchesOriginalStep(slice) &&
        isSameFullHeadMprAnnotationPlane({
          isSeries: slice.Source.case !== 'View',
          left: candidate,
          right: view,
        })
      );
    });
  const newRequest = makeFullHeadMprViewSelectionRequest({ snapshot, toothID });
  const target = existing
    ? { case: 'SliceID' as const, value: existing.ID }
    : originalMatches
      ? original.target
      : newRequest.Target;
  const seriesContext = resolveFullHeadMprSeriesContext({
    slice: existing,
    target,
  });
  const displayView = makeFullHeadMprViewMessage(view, seriesContext);
  const annotationView =
    existing?.EditorState?.AnnotationView ??
    (existing?.Source.case === 'View' ? existing.Source.value : null) ??
    (originalMatches
      ? makeFullHeadMprViewMessage(original.view, seriesContext)
      : displayView);
  return new SetToothFullHeadMPRSliceEditorStateReq({
    ToothID: toothID,
    Geometry:
      existing?.Geometry ??
      (originalMatches ? original.geometry : newRequest.Geometry),
    EditorState: { AnnotationView: annotationView, DisplayView: displayView },
    Target: target,
  });
};
